import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { trackAdInteraction } from '../analytics';
import {
  AFFILIATE_BOOKS,
  AFFILIATE_DISCLOSURE,
  AFFILIATE_SUPPORT_MESSAGE,
  SHOWCASE_CTA,
  SHOWCASE_HEADING,
  SHOWCASE_SUBHEADING,
  resolveLocale,
  withAmazonTag,
} from '../affiliate/products';

interface BookShowcaseProps {
  className?: string;
}

// Shown if a cover image is missing or fails to load, so the layout never breaks.
const FALLBACK_IMAGE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="300"><rect width="100%" height="100%" fill="%23e5e7eb"/><text x="50%" y="50%" font-family="sans-serif" font-size="16" fill="%239ca3af" text-anchor="middle" dominant-baseline="middle">Livro</text></svg>`
  );

export function BookShowcase({ className = '' }: BookShowcaseProps) {
  const { i18n } = useTranslation();
  const locale = resolveLocale(i18n.language);

  if (AFFILIATE_BOOKS.length === 0) {
    return null;
  }

  return (
    <section className={`w-full ${className}`} aria-label={SHOWCASE_HEADING[locale]}>
      <div className="text-center mb-6">
        <h3 className="text-foreground/90">{SHOWCASE_HEADING[locale]}</h3>
        <p className="text-muted-foreground text-sm mt-1">
          {SHOWCASE_SUBHEADING[locale]}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {AFFILIATE_BOOKS.map((book) => (
          <a
            key={book.id}
            href={withAmazonTag(book.url)}
            target="_blank"
            rel="sponsored nofollow noopener noreferrer"
            onClick={() => trackAdInteraction(`book:${book.id}`)}
            className="group block"
          >
            <Card className="h-full overflow-hidden transition-shadow hover:shadow-md">
              <div className="aspect-[2/3] bg-muted overflow-hidden">
                <img
                  src={book.image}
                  alt={book.title}
                  loading="lazy"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = FALLBACK_IMAGE;
                  }}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
              </div>
              <div className="p-3">
                <h4 className="text-sm text-foreground/90 line-clamp-2">
                  {book.title}
                </h4>
                {book.author && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {book.author}
                  </p>
                )}
                <span className="inline-block mt-2 text-xs text-primary group-hover:underline">
                  {SHOWCASE_CTA[locale]} →
                </span>
              </div>
            </Card>
          </a>
        ))}
      </div>

      {/* Warm support message + required Amazon Associates / FTC disclosure. */}
      <p className="text-sm text-muted-foreground max-w-xl mx-auto mt-6 text-center">
        {AFFILIATE_SUPPORT_MESSAGE[locale]}
      </p>
      <p className="text-[10px] leading-tight text-muted-foreground/60 mt-1 text-center">
        {AFFILIATE_DISCLOSURE[locale]}
      </p>
    </section>
  );
}
